import {
    MultiSelect as MantineMultiSelect,
    type ComboboxData,
    type MultiSelectProps as MantineMultiSelectProps
} from "@mantine/core";
import {
    Scope,
    useFieldInput,
    useLookupFilter,
    type FieldActions,
    type LookupFilter,
    type ValidationError,
    type ValidationErrorDisplayMode
} from "@ngt-form/core";
import { getInputClassName, getInputError, useMantineFormOptions } from "@ngt-form/mantine";
import { useMemo, type FocusEvent } from "react";

import classes from "./ObjectArrayMultiSelectInput.module.css";

export type NumericPropertyKey<TItem extends object> = {
    [TKey in keyof TItem]-?: NonNullable<TItem[TKey]> extends number ? TKey : never;
}[keyof TItem] &
    string;

export type ObjectArrayMultiSelectInputProps<
    TItem extends object,
    TFormValue extends object = object,
    TValidationError extends ValidationError = ValidationError,
    TFormMetadata extends object = object,
    TLookupItem = unknown
> = Partial<Omit<MantineMultiSelectProps, "value" | "onChange" | "onBlur" | "onFocus">> & {
    valueProperty: NumericPropertyKey<TItem>;
    createItem?: (value: number) => TItem;
    onChange?: (
        value: TItem[],
        fieldActions: FieldActions<
            TFormValue,
            TValidationError,
            TFormMetadata,
            TItem[],
            TLookupItem
        >
    ) => void;
    onBlur?: (
        event: FocusEvent<HTMLInputElement>,
        fieldActions: FieldActions<
            TFormValue,
            TValidationError,
            TFormMetadata,
            TItem[],
            TLookupItem
        >
    ) => void;
    onFocus?: (
        event: FocusEvent<HTMLInputElement>,
        fieldActions: FieldActions<
            TFormValue,
            TValidationError,
            TFormMetadata,
            TItem[],
            TLookupItem
        >
    ) => void;
    validationErrorDisplayMode?: ValidationErrorDisplayMode | null;
    hideLabel?: boolean | null;
    lookupFilter?: LookupFilter<TItem[], TLookupItem> | null;
    preserveFilteredOutSelections?: boolean | null;
};

const noData: ComboboxData = [];

/**
 * Coerces the configured item property to a finite number.
 * Postconditions: Nonfinite results return undefined; standard Number coercion is retained, including
 * zero for null or empty strings. Conversion exceptions propagate.
 */
const getNumericValue = <TItem extends object>(
    item: TItem,
    valueProperty: NumericPropertyKey<TItem>
) => {
    const value = Number(item[valueProperty]);
    return Number.isFinite(value) ? value : undefined;
};

/**
 * Reconciles numeric selections with existing item objects and optional hidden selections.
 * Postconditions: Finite selected values retain their order and duplicates. The first initial object
 * wins over the first current object; unmatched values use createItem or a minimal property object.
 * Preserved, unselected current objects append in current order without cloning or deduplication.
 * Inputs are not mutated by this helper; caller factory/conversion failures propagate.
 */
export const reconcileObjectArraySelection = <TItem extends object>(
    selectedValues: readonly string[],
    initialValue: readonly TItem[] | null | undefined,
    currentValue: readonly TItem[] | null | undefined,
    valueProperty: NumericPropertyKey<TItem>,
    createItem?: (value: number) => TItem,
    preservedValues?: ReadonlySet<number>
): TItem[] => {
    const initialItemsByValue = new Map<number, TItem>();
    const currentItemsByValue = new Map<number, TItem>();

    for (const item of initialValue ?? []) {
        const value = getNumericValue(item, valueProperty);
        if (value !== undefined && !initialItemsByValue.has(value)) {
            initialItemsByValue.set(value, item);
        }
    }

    for (const item of currentValue ?? []) {
        const value = getNumericValue(item, valueProperty);
        if (value !== undefined && !currentItemsByValue.has(value)) {
            currentItemsByValue.set(value, item);
        }
    }

    const selectedItems = selectedValues.flatMap(selectedValue => {
        const value = Number(selectedValue);
        if (!Number.isFinite(value)) {
            return [];
        }

        const existingItem = initialItemsByValue.get(value) ?? currentItemsByValue.get(value);
        if (existingItem) {
            return [existingItem];
        }

        return [
            createItem?.(value) ??
                ({
                    [valueProperty]: value
                } as TItem)
        ];
    }
    );

    if (!preservedValues?.size) {
        return selectedItems;
    }

    const selectedValueSet = new Set(
        selectedItems.flatMap(item => {
            const value = getNumericValue(item, valueProperty);
            return value === undefined ? [] : [value];
        }
        )
    );

    return [
        ...selectedItems,
        ...(currentValue ?? []).filter(item => {
            const value = getNumericValue(item, valueProperty);
            return (
                value !== undefined &&
                preservedValues.has(value) &&
                !selectedValueSet.has(value)
            );
        }
        )
    ];
};

/**
 * Binds an object-array field to string-valued multi-selection while retaining object identities.
 * Preconditions: Form context supplies field actions; valueProperty identifies the numeric key.
 * Postconditions: Caller data overrides lookup options. Optional preservation hides filtered-out IDs
 * from the widget but retains them when reconciling changes. Field update precedes the caller callback
 * and requests dirty/focused/touched notifications; returning does not establish persistence.
 * Blur updates focused/touched state before its callback; focus does not mark touched.
 */
export const ObjectArrayMultiSelectInput = <
    TItem extends object,
    TFormValue extends object = object,
    TValidationError extends ValidationError = ValidationError,
    TFormMetadata extends object = object,
    TLookupItem = unknown
>({
    valueProperty,
    createItem,
    disabled: componentDisabled,
    label: componentLabel,
    data: componentData,
    onChange,
    onFocus,
    onBlur,
    className: componentClassName,
    validationErrorDisplayMode: componentValidationErrorDisplayMode,
    hideLabel,
    size,
    lookupFilter,
    preserveFilteredOutSelections,
    ...props
}: ObjectArrayMultiSelectInputProps<
    TItem,
    TFormValue,
    TValidationError,
    TFormMetadata,
    TLookupItem
>) => {
    const {
        actions,
        snapshot,
        validationResult,
        disabled,
        label,
        validationErrorTypeName,
        validationErrors
    } = useFieldInput<TFormValue, TValidationError, TFormMetadata, TItem[], TLookupItem, string[]>({
        scope: Scope.Context,
        label: componentLabel,
        validationErrorDisplayMode: componentValidationErrorDisplayMode,
        disabled: componentDisabled,
        subscription: {
            value: true,
            label: true,
            lookup: true,
            initialValue: true,
            dirty: false,
            focused: false,
            touched: false
        }
    });

    const className = useMemo(
        () => getInputClassName(componentClassName, validationErrorTypeName, classes),
        [componentClassName, validationErrorTypeName]
    );

    const mantineFormOptions = useMantineFormOptions();

    const filteredLookup = useLookupFilter<TItem[], TLookupItem>(
        snapshot.propertyPath,
        snapshot.lookup,
        lookupFilter,
        snapshot.value
    );

    /**
     * Chooses caller options or finite numeric options from the filtered lookup.
     * Postconditions: Even an empty supplied data array wins; nonfinite lookup values are omitted.
     */
    const data = useMemo<ComboboxData>(
        () => {
            if (componentData) {
                return componentData;
            }

            if (!filteredLookup?.items) {
                return noData;
            }

            return filteredLookup.items.flatMap(item => {
                const value = Number(filteredLookup.getValue(item));
                if (!Number.isFinite(value)) {
                    return [];
                }

                return [{ label: filteredLookup.getLabel(item), value: String(value) }];
            }
            );
        },
        [componentData, filteredLookup]
    );

    /**
     * Captures visible lookup IDs only when hidden-selection preservation is enabled.
     */
    const filteredLookupValues = useMemo(
        () => {
            if (!preserveFilteredOutSelections || !filteredLookup?.items) {
                return undefined;
            }

            return new Set(
                filteredLookup.items.flatMap(item => {
                    const value = Number(filteredLookup.getValue(item));
                    return Number.isFinite(value) ? [value] : [];
                }
                )
            );
        },
        [filteredLookup, preserveFilteredOutSelections]
    );

    /**
     * Identifies finite currently selected IDs absent from the available filtered lookup.
     */
    const filteredOutSelectionValues = useMemo(
        () => {
            if (!filteredLookupValues) {
                return undefined;
            }

            return new Set(
                (snapshot.value ?? []).flatMap(item => {
                    const value = getNumericValue(item, valueProperty);
                    return value !== undefined && !filteredLookupValues.has(value) ? [value] : [];
                }
                )
            );
        },
        [filteredLookupValues, snapshot.value, valueProperty]
    );

    /**
     * Projects visible selected object IDs to strings, retaining order and duplicates.
     */
    const value = useMemo(
        () =>
            (snapshot.value ?? []).flatMap(item => {
                const itemValue = getNumericValue(item, valueProperty);
                return itemValue === undefined || filteredOutSelectionValues?.has(itemValue)
                    ? []
                    : [String(itemValue)];
            }
            ),
        [filteredOutSelectionValues, snapshot.value, valueProperty]
    );

    return (
        <MantineMultiSelect
            {...props}
            size={size ?? mantineFormOptions.inputs.defaultSize}
            className={className}
            label={hideLabel ? undefined : label}
            onBlur={event => {
                actions.setFocused(false, { notify: true, updateTouched: true });
                onBlur?.(event, actions);
            }}
            onFocus={event => {
                actions.setFocused(true, { notify: true, updateTouched: false });
                onFocus?.(event, actions);
            }}
            onChange={selectedValues => {
                const newValue = reconcileObjectArraySelection(
                    selectedValues,
                    snapshot.initialValue,
                    snapshot.value,
                    valueProperty,
                    createItem,
                    filteredOutSelectionValues
                );

                actions.setValue(newValue, {
                    notify: true,
                    updateDirty: true,
                    updateFocused: true,
                    updateTouched: true
                });
                onChange?.(newValue, actions);
            }}
            data={data}
            value={value}
            disabled={disabled}
            error={getInputError(validationErrors, !!validationResult)}
        />
    );
};

export default ObjectArrayMultiSelectInput;
